import { parseNumericInput } from '../../utils/numberInput';
import { deriveOperationType } from './constants';
import { newBooth } from './defaults';

function esc(value) {
  const stringValue = value == null ? '' : String(value);
  if (/[;"'\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

function sanitizeCsvCell(value) {
  if (value == null) return '';

  const stringValue = String(value);

  if (
    /^-?\d+(?:[.,]\d+)?$/.test(stringValue) ||
    /^-?\d{1,3}(?:\.\d{3})+,\d+$/.test(stringValue) ||
    /^-?\d{1,3}(?:,\d{3})+\.\d+$/.test(stringValue)
  ) {
    return stringValue;
  }

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(stringValue)) {
    return stringValue;
  }

  return stringValue
    .replace(/[^\p{L}\p{N}_]+/gu, '_')
    .replace(/^_+|_+$/g, '');
}

function toCsvValue(value) {
  if (Array.isArray(value)) {
    return value.join('|');
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return value ?? '';
}

function unitLabel(unit) {
  const labels = {
    kwh: 'kWh',
    l: 'l',
    liter: 'Liter',
    kg: 'kg',
    chf: 'CHF',
    unknown: '',
  };

  return labels[unit] || unit || '';
}

function normalizeEnergyUnitForBackend(type, unit) {
  return ['Heizöl', 'Erdgas', 'Flüssiggas (LPG)'].includes(type) && unit === 'liter' ? 'l' : unit;
}

function normalizeHeatingSystemForBackend(system) {
  const isHeatingOil = system.type === 'Heizöl';

  return {
    type: isHeatingOil ? 'heizöl' : system.type,
    unit: normalizeEnergyUnitForBackend(system.type, system.unit),
    value: system.value,
  };
}

const REPAIR_TYPE_FIELD_KEYS = {
  'Spot Repair': 'spot_repair',
  'Blech / Richten': 'blech_richten',
  ['Dr\u00fccken']: 'druecken',
  Stossstangen: 'stossstangen',
  Glas: 'glas',
  ['Struktursch\u00e4den']: 'strukturschaeden',
};

function repairTypeFieldKey(type) {
  const mappedKey = REPAIR_TYPE_FIELD_KEYS[type];

  if (mappedKey) {
    return mappedKey;
  }

  return String(type || '')
    .trim()
    .toLowerCase()
    .replace(/\u00e4/g, 'ae')
    .replace(/\u00f6/g, 'oe')
    .replace(/\u00fc/g, 'ue')
    .replace(/\u00df/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function reportingStartDate(year) {
  return year ? `01.01.${year}` : '';
}

function reportingEndDate(year) {
  return year ? `31.12.${year}` : '';
}

function buildRow({
  userId,
  reportingYear,
  recordIndex,
  table,
  field,
  value,
  unit = '',
  comment = '',
}) {
  return [
    userId,
    reportingYear,
    recordIndex,
    table,
    field,
    sanitizeCsvCell(toCsvValue(value)),
    sanitizeCsvCell(unit),
    sanitizeCsvCell(comment),
  ].map(esc).join(';');
}

export function buildDataCollectionCsv(formData, options = {}) {
  const userId = options.userId || formData.userId || '';
  const createdAt = new Date().toISOString();
  const rows = [
    'user_id;reporting_period;record_index;table;field;value;unit;comment',
  ];

  function add(table, field, value, unit = '', comment = '', recordIndex = '') {
    rows.push(
      buildRow({
        userId,
        reportingYear: formData.reportingYear,
        recordIndex,
        table,
        field,
        value,
        unit,
        comment,
      })
    );
  }

  add('EXPORT', 'created_at', createdAt, 'timestamp');
  add('USER', 'user_id', userId, 'uuid');
  add('USER', 'company_name', options.companyName || '', 'text');

  add('DATA_SOURCE', 'gcs_requested', formData.gcsRequested, 'boolean');
  add('DATA_SOURCE', 'silverdat_requested', formData.silverdatRequested, 'boolean');

  add('OPERATION_YEAR', 'start_date', reportingStartDate(formData.reportingYear), 'date');
  add('OPERATION_YEAR', 'end_date', reportingEndDate(formData.reportingYear), 'date');
  add('OPERATION_YEAR', 'activity_body_work_share', formData.activitySpenglerarbeiten, 'percent');
  add('OPERATION_YEAR', 'activity_paint_work_share', formData.activityLackierarbeiten, 'percent');
  add('OPERATION_YEAR', 'activity_other_share', formData.activityWeitere, 'percent');
  add(
    'OPERATION_YEAR',
    'business_type',
    deriveOperationType(
      formData.activitySpenglerarbeiten,
      formData.activityLackierarbeiten,
      formData.activityWeitere
    ),
    'text',
    'derived'
  );
  add('OPERATION_YEAR', 'garage_type', formData.garageType, 'text');
  add('OPERATION_YEAR', 'garage_brands', formData.garageBrands, 'text');
  add('OPERATION_YEAR', 'building_relationship', formData.buildingRelationship, 'text');
  add('OPERATION_YEAR', 'has_mechanical_workshop', formData.hasMechanicalWorkshop, 'boolean');
  add('OPERATION_YEAR', 'has_vehicle_sales', formData.hasVehicleSales, 'boolean');
  add('OPERATION_YEAR', 'has_washing_system', formData.hasWashingSystem, 'boolean');
  add('OPERATION_YEAR', 'has_fuel_station', formData.hasFuelStation, 'boolean');
  add('OPERATION_YEAR', 'has_public_charging_stations', formData.hasPublicChargingStations, 'boolean');
  add('OPERATION_YEAR', 'public_charging_points_count', formData.publicChargingPointsCount, 'count');
  add('OPERATION_YEAR', 'productive_fte', formData.productiveFte, 'fte');
  add('OPERATION_YEAR', 'total_area', formData.totalAreaM2, 'm2');
  add('OPERATION_YEAR', 'productive_area', formData.productiveAreaM2, 'm2');
  add('OPERATION_YEAR', 'sold_hours_total', formData.soldHoursTotal, 'hours/year');
  add('OPERATION_YEAR', 'split_sold_hours', formData.splitSoldHours, 'boolean');
  add('OPERATION_YEAR', 'sold_hours_paint', formData.soldHoursPaint, 'hours/year');
  add('OPERATION_YEAR', 'sold_hours_body', formData.soldHoursBody, 'hours/year');
  add('OPERATION_YEAR', 'annual_revenue', formData.annualRevenue, 'CHF/year');
  add('OPERATION_YEAR', 'split_revenue', formData.splitRevenue, 'boolean');
  add('OPERATION_YEAR', 'revenue_paint', formData.revenuePaint, 'CHF/year');
  add('OPERATION_YEAR', 'revenue_body', formData.revenueBody, 'CHF/year');
  add('OPERATION_YEAR', 'average_hourly_rate', formData.averageHourlyRate, 'CHF/hour');
  add('OPERATION_YEAR', 'split_hourly_rate', formData.splitHourlyRate, 'boolean');
  add('OPERATION_YEAR', 'hourly_rate_paint', formData.hourlyRatePaint, 'CHF/hour');
  add('OPERATION_YEAR', 'hourly_rate_body', formData.hourlyRateBody, 'CHF/hour');

  add('ELECTRICITY', 'electricity_mix', formData.electricityMix, 'text');
  add('ELECTRICITY', 'electricity_input_unit', formData.electricityUnit, 'text');
  add('ELECTRICITY', 'electricity_input_value', formData.electricityValue, unitLabel(formData.electricityUnit));
  add(
    'ELECTRICITY',
    'electricity_upload_file_names',
    (formData.electricityInvoiceFiles || []).map(f => f.fileName).join('; '),
    'text'
  );
  add('ELECTRICITY', 'electricity_invoice_period_start', formData.electricityInvoicePeriodStart, 'text');
  add('ELECTRICITY', 'electricity_invoice_period_end', formData.electricityInvoicePeriodEnd, 'text');
  add('ELECTRICITY', 'pv_status', formData.pvStatus, 'text');
  add('ELECTRICITY', 'pv_production', formData.pvProductionKwh, 'kWh/year');
  add('ELECTRICITY', 'pv_feed_in', formData.pvFeedInKwh, 'kWh/year');
  add(
    'ELECTRICITY',
    'pv_self_consumption',
    formData.pvStatus === 'vorhanden'
      ? Math.max(
        0,
        (parseFloat(formData.pvProductionKwh) || 0) -
        (parseFloat(formData.pvFeedInKwh) || 0)
      )
      : '',
    'kWh/year',
    formData.pvStatus === 'vorhanden' ? 'derived' : ''
  );

  const heatingSystems = Array.isArray(formData.heatingSystems)
    ? formData.heatingSystems
    : [];

  heatingSystems.forEach((system, index) => {
    const recordIndex = index + 1;
    const prefix = `heating_${index + 1}`;
    const backendSystem = normalizeHeatingSystemForBackend(system);

    add('HEATING_SYSTEM', `${prefix}_type`, backendSystem.type, 'text', '', recordIndex);
    add('HEATING_SYSTEM', `${prefix}_input_unit`, backendSystem.unit, 'text', '', recordIndex);
    add('HEATING_SYSTEM', `${prefix}_input_value`, backendSystem.value, unitLabel(backendSystem.unit), '', recordIndex);
  });

  add('HEATING', 'heating_upload_file_name', formData.heatingUploadFileName, 'text');
  add('HEATING', 'gas_shutdown_planned', formData.gasShutdownPlanned, 'boolean');
  add('HEATING', 'gas_shutdown_year', formData.gasShutdownYear, 'year');
  add('HEATING', 'oil_heating_replacement_planned', formData.oilHeatingReplacementPlanned, 'boolean');
  add('HEATING', 'oil_heating_replacement_year', formData.oilHeatingReplacementYear, 'year');

  const paintBooths = Array.isArray(formData.paintBooths)
    ? formData.paintBooths
    : [];

  paintBooths.forEach((booth, index) => {
    const recordIndex = index + 1;
    const prefix = `booth_${index + 1}`;
    const energyConsumptionUnit = normalizeEnergyUnitForBackend(
      booth.burnerEnergy,
      booth.energyConsumptionUnit
    );

    add('PAINT_BOOTH', `${prefix}_technical_plate_file_name`, booth.photoFileName, 'text', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_build_year`, booth.buildYear, 'year', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_type`, booth.type, 'text', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_separately_heated`, booth.separatelyHeated, 'text', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_burner_energy`, booth.burnerEnergy, 'text', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_energy_consumption_unit`, energyConsumptionUnit, 'text', '', recordIndex);
    add(
      'PAINT_BOOTH',
      `${prefix}_energy_consumption`,
      booth.energyConsumption,
      unitLabel(energyConsumptionUnit),
      '',
      recordIndex
    );
    const efficiencyTech = booth.efficiencyTech || [];
    add('PAINT_BOOTH', `${prefix}_has_frequenzumformer`, efficiencyTech.includes('Frequenzumformer'), 'boolean', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_has_sparschaltung`, efficiencyTech.includes('Sparschaltung'), 'boolean', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_has_wrg`, efficiencyTech.includes('WRG'), 'boolean', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_drying_type`, booth.dryingType, 'text', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_drying_temperature`, booth.dryingTemp, 'C', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_drying_time`, booth.dryingTime, 'minutes', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_cycles_per_day`, booth.cyclesPerDay, 'count/day', '', recordIndex);
    add('PAINT_BOOTH', `${prefix}_idle_runs`, booth.idleRuns, 'count', '', recordIndex);
  });

  add('PAINT', 'paint_liters', formData.paintLiters, 'liter/year');
  add('PAINT', 'air_drying_paint_share', formData.airDryingPaintShare, 'percent');
  add(
    'PAINT',
    'conventional_paint_share',
    Math.max(0, 100 - (parseFloat(formData.airDryingPaintShare) || 0)),
    'percent',
    'derived'
  );

  Object.entries(formData.materials || {}).forEach(([key, value]) => {
    add('CONSUMABLE_SHARE', `${key}_share`, value, 'percent');
  });

  add('REPAIR_ACTIVITY', 'repairs_per_year', formData.repairsPerYear, 'count/year');
  add('REPAIR_ACTIVITY', 'repair_types', formData.repairTypes || [], 'list');

  (formData.repairTypes || []).forEach(type => {
    add(
      'REPAIR_ACTIVITY',
      `repair_quota_${repairTypeFieldKey(type)}`,
      formData.repairTypeShares?.[type] ?? '',
      'percent'
    );
  });

  add('REPAIR_ACTIVITY', 'spare_parts_tendency', formData.sparePartsTendency, 'text');
  add('REPAIR_ACTIVITY', 'recycling_parts_share', formData.recyclingPartsShare, 'percent');
  add('REPAIR_ACTIVITY', 'spare_parts_value', formData.sparePartsValueCHF, 'CHF/year');

  add('FLEET', 'fleet_file_name', formData.fleetFileName, 'text');
  add('FLEET', 'fleet_import_successful', formData.fleetImportSuccessful, 'boolean');
  add('FLEET', 'fleet_import_message', formData.fleetImportMessage, 'text');
  add('FLEET', 'fleet_import_error', formData.fleetImportError, 'text');

  add('FLEET', 'combustion_count', formData.combustionCount, 'count');
  add('FLEET', 'combustion_average_age', parseNumericInput(formData.combustionCount) > 0 ? formData.combustionAvgAge : '', 'years');
  add('FLEET', 'combustion_average_km', parseNumericInput(formData.combustionCount) > 0 ? formData.combustionAvgKm : '', 'km/year');
  add('FLEET', 'electric_count', formData.electricCount, 'count');
  add('FLEET', 'electric_average_age', parseNumericInput(formData.electricCount) > 0 ? formData.electricAvgAge : '', 'years');
  add('FLEET', 'electric_average_km', parseNumericInput(formData.electricCount) > 0 ? formData.electricAvgKm : '', 'km/year');
  add('FLEET', 'hybrid_count', formData.hybridCount, 'count');
  add('FLEET', 'hybrid_average_age', parseNumericInput(formData.hybridCount) > 0 ? formData.hybridAvgAge : '', 'years');
  add('FLEET', 'hybrid_average_km', parseNumericInput(formData.hybridCount) > 0 ? formData.hybridAvgKm : '', 'km/year');
  add('FLEET', 'fleet_ownership', formData.fleetOwnership, 'text');

  add('WASTE', 'waste_categories', formData.wasteCategories || [], 'list');
  add('WASTE', 'body_parts_third_party_share', formData.bodyPartsThirdPartyShare, 'percent');
  add(
    'WASTE',
    'body_parts_mixed_container_share',
    Math.max(0, 100 - (parseFloat(formData.bodyPartsThirdPartyShare) || 0)),
    'percent',
    'derived'
  );

  add('COMPRESSED_AIR', 'has_compressed_air', formData.hasCompressedAir, 'boolean');
  add('COMPRESSED_AIR', 'compressor_type', formData.compressorType, 'text');
  add('COMPRESSED_AIR', 'compressor_build_year', formData.compressorBuildYear, 'year');
  add('COMPRESSED_AIR', 'compressed_air_pressure', formData.compressedAirPressureBar, 'bar');
  add('COMPRESSED_AIR', 'compressor_heat_recovery', formData.compressorHeatRecovery, 'text');
  add('COMPRESSED_AIR', 'compressed_air_leaks', formData.compressedAirLeaks, 'text');
  add('COMPRESSED_AIR', 'pneumatic_grinders', formData.pneumaticGrinders, 'text');

  add('INFRASTRUCTURE', 'lighting', formData.lighting, 'text');

  return rows.join('\n');
}

function parseBoolean(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'boolean') return value;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

function parseNumber(value) {
  if (value === null || value === undefined || value === '') return '';
  const trimmed = String(value).trim();
  if (trimmed === '') return '';

  const decimalSeparatorIndex = Math.max(
    trimmed.lastIndexOf('.'),
    trimmed.lastIndexOf(',')
  );
  const normalized = decimalSeparatorIndex === -1
    ? trimmed
    : `${trimmed
        .slice(0, decimalSeparatorIndex)
        .replace(/[.,]/g, '')}.${trimmed.slice(decimalSeparatorIndex + 1)}`;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : trimmed;
}

function unescapeCsvCell(value) {
  if (value === undefined || value === null) return '';
  let text = String(value).trim();

  if (text.startsWith('"') && text.endsWith('"')) {
    text = text.slice(1, -1).replace(/""/g, '"');
  }

  return text;
}

function parseCsvLines(csvText) {
  const lines = csvText
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter(line => line.trim() !== '');

  if (lines.length === 0) return [];

  return lines.map(line => {
    const cells = [];
    let current = '';
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const char = line[index];

      if (char === '"') {
        if (inQuotes && line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ';' && !inQuotes) {
        cells.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    cells.push(current);
    return cells.map(unescapeCsvCell);
  });
}

function ensureArray(value) {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split('|')
    .map(entry => entry.trim())
    .filter(Boolean);
}

function readFieldValue(rows, table, field) {
  const row = rows.find(item => item.table === table && item.field === field);
  return row ? row.value : '';
}

function readFieldRows(rows, table, prefix) {
  return rows.filter(item => item.table === table && item.field.startsWith(prefix));
}

function normalizeFromBackendUnit(type, unit) {
  if (!type || !unit) return unit || '';

  const normalizedType = String(type).trim();
  const normalizedUnit = String(unit).trim();

  if (
    ['Heizöl', 'Erdgas', 'Flüssiggas (LPG)', 'heizöl', 'erdgas', 'flüssiggas (lpg)']
      .includes(normalizedType) &&
    normalizedUnit === 'l'
  ) {
    return 'liter';
  }

  return normalizedUnit;
}

function normalizeBackendText(value) {
  if (value === null || value === undefined) return '';

  const text = String(value).trim();
  if (!text) return '';

  const mapping = {
    heizöl: 'Heizöl',
    erdgas: 'Erdgas',
    'flüssiggas_lpg': 'Flüssiggas (LPG)',
    'flüssiggas lpg': 'Flüssiggas (LPG)',
    'holz_pellets': 'Holz/Pellets',
    'druecken': 'Drücken',
    'strukturschaeden': 'Strukturschäden',
    'drittfirma_leasing_miete': 'Drittfirma(Leasing/Miete)',
    'drittfirma_leasing_miete ': 'Drittfirma(Leasing/Miete)',
  };

  const lower = text.toLowerCase();
  if (mapping[lower]) return mapping[lower];

  if (text.includes('_')) {
    const parts = text.split('_').filter(Boolean);
    if (parts.length > 1 && /^(drittfirma|leasing|miete|eigentum|fremd|partner)$/i.test(parts[0])) {
      return `${parts[0]}(${parts.slice(1).join('/')})`;
    }
    return parts.join(' ');
  }

  return text;
}

function normalizeDisplayText(value) {
  if (value === null || value === undefined) return '';

  return String(value)
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
}

function normalizePaintBoothType(value) {
  const normalized = String(value || '').trim();
  const mapping = {
    standard_kabine: 'Standard-Kabine',
    vorbereitung_spot_repair: 'Vorbereitung & Spot-Repair',
  };

  return mapping[normalized.toLowerCase()] || normalizeDisplayText(normalized);
}

function normalizeBoothHeating(value) {
  const normalized = String(value || '').trim().toLowerCase();
  const mapping = {
    ja: 'Ja',
    nein_in_gebäudeheizung: 'Nein, in Gebäudeheizung',
    weiss_nicht: 'Weiss nicht',
  };

  return mapping[normalized] || normalizeDisplayText(value);
}

function normalizeDryingType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  const mapping = {
    konventionell_warmluft: 'Konventionell / Warmluft',
    infrarot: 'Infrarot',
    lufttrocknend: 'Lufttrocknend',
  };

  return mapping[normalized] || normalizeDisplayText(value);
}

export function parseDataCollectionCsv(csvText) {
  if (typeof csvText === 'string') {
    const trimmed = csvText.trim();

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsedJson = JSON.parse(trimmed);

        if (Array.isArray(parsedJson)) {
          return parsedJson[0] || {};
        }

        if (parsedJson && typeof parsedJson === 'object') {
          const reportingData = parsedJson.reporting_data || parsedJson.data || parsedJson.rows;

          if (Array.isArray(reportingData) && reportingData.length > 0) {
            const firstRecord = reportingData[0];

            if (firstRecord && typeof firstRecord === 'object') {
              return {
                ...firstRecord,
                companyName: parsedJson.companyName || parsedJson.company_name || firstRecord.companyName || '',
              };
            }
          }

          if (parsedJson.companyName || parsedJson.company_name || parsedJson.reportingYear) {
            return parsedJson;
          }
        }
      } catch {
        // Not valid JSON; continue with CSV parsing below.
      }
    }
  }

  const rows = parseCsvLines(csvText);

  if (rows.length < 2) {
    return {};
  }

  const header = rows[0];
  const dataRows = rows.slice(1).map(columns => {
    const item = {};
    for (let index = 0; index < header.length; index += 1) {
      item[header[index]] = columns[index] ?? '';
    }
    return item;
  });

  const parsed = {};

  const reportingYearValue = readFieldValue(dataRows, 'OPERATION_YEAR', 'start_date');
  const match = String(reportingYearValue || '').match(/(\d{4})$/);
  if (match) {
    parsed.reportingYear = Number(match[1]);
  }

  parsed.activitySpenglerarbeiten = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'activity_body_work_share')) || 0;
  parsed.activityLackierarbeiten = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'activity_paint_work_share')) || 0;
  parsed.activityWeitere = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'activity_other_share')) || 0;

  parsed.garageType = readFieldValue(dataRows, 'OPERATION_YEAR', 'garage_type');
  parsed.garageBrands = readFieldValue(dataRows, 'OPERATION_YEAR', 'garage_brands');
  parsed.buildingRelationship = readFieldValue(dataRows, 'OPERATION_YEAR', 'building_relationship');
  parsed.hasMechanicalWorkshop = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'has_mechanical_workshop'));
  parsed.hasVehicleSales = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'has_vehicle_sales'));
  parsed.hasWashingSystem = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'has_washing_system'));
  parsed.hasFuelStation = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'has_fuel_station'));
  parsed.hasPublicChargingStations = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'has_public_charging_stations'));
  parsed.publicChargingPointsCount = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'public_charging_points_count'));
  parsed.productiveFte = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'productive_fte'));
  parsed.totalAreaM2 = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'total_area'));
  parsed.productiveAreaM2 = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'productive_area'));
  parsed.soldHoursTotal = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'sold_hours_total'));
  parsed.splitSoldHours = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'split_sold_hours'));
  parsed.soldHoursPaint = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'sold_hours_paint'));
  parsed.soldHoursBody = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'sold_hours_body'));
  parsed.annualRevenue = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'annual_revenue'));
  parsed.splitRevenue = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'split_revenue'));
  parsed.revenuePaint = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'revenue_paint'));
  parsed.revenueBody = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'revenue_body'));
  parsed.averageHourlyRate = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'average_hourly_rate'));
  parsed.splitHourlyRate = parseBoolean(readFieldValue(dataRows, 'OPERATION_YEAR', 'split_hourly_rate'));
  parsed.hourlyRatePaint = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'hourly_rate_paint'));
  parsed.hourlyRateBody = parseNumber(readFieldValue(dataRows, 'OPERATION_YEAR', 'hourly_rate_body'));

  parsed.gcsRequested = parseBoolean(readFieldValue(dataRows, 'DATA_SOURCE', 'gcs_requested'));
  parsed.silverdatRequested = parseBoolean(readFieldValue(dataRows, 'DATA_SOURCE', 'silverdat_requested'));

  parsed.electricityMix = readFieldValue(dataRows, 'ELECTRICITY', 'electricity_mix');
  parsed.electricityUnit = readFieldValue(dataRows, 'ELECTRICITY', 'electricity_input_unit') || 'kwh';
  parsed.electricityValue = parseNumber(readFieldValue(dataRows, 'ELECTRICITY', 'electricity_input_value'));
  parsed.pvStatus = readFieldValue(dataRows, 'ELECTRICITY', 'pv_status');
  parsed.pvProductionKwh = parseNumber(readFieldValue(dataRows, 'ELECTRICITY', 'pv_production'));
  parsed.pvFeedInKwh = parseNumber(readFieldValue(dataRows, 'ELECTRICITY', 'pv_feed_in'));

  const heatingRows = readFieldRows(dataRows, 'HEATING_SYSTEM', 'heating_');
  const heatingSystems = [];
  const heatingIndexes = [...new Set(heatingRows.map(item => Number(String(item.record_index || '').trim() || 0)).filter(n => n > 0))];

  heatingIndexes.forEach(index => {
    const type = normalizeBackendText(readFieldValue(dataRows, 'HEATING_SYSTEM', `heating_${index}_type`));
    const unit = readFieldValue(dataRows, 'HEATING_SYSTEM', `heating_${index}_input_unit`);
    const value = parseNumber(readFieldValue(dataRows, 'HEATING_SYSTEM', `heating_${index}_input_value`));

    if (type || unit || value) {
      heatingSystems.push({
        id: `restored-${index}`,
        type,
        unit: normalizeFromBackendUnit(type, unit),
        value,
      });
    }
  });

  parsed.heatingSystems = heatingSystems.length > 0 ? heatingSystems : [{ id: 'restored-1', type: '', unit: 'kwh', value: '' }];
  parsed.gasShutdownPlanned = parseBoolean(readFieldValue(dataRows, 'HEATING', 'gas_shutdown_planned'));
  parsed.gasShutdownYear = readFieldValue(dataRows, 'HEATING', 'gas_shutdown_year');
  parsed.oilHeatingReplacementPlanned = parseBoolean(readFieldValue(dataRows, 'HEATING', 'oil_heating_replacement_planned'));
  parsed.oilHeatingReplacementYear = readFieldValue(dataRows, 'HEATING', 'oil_heating_replacement_year');

  const boothRows = readFieldRows(dataRows, 'PAINT_BOOTH', 'booth_');
  const boothIndexes = [...new Set(boothRows.map(item => Number(String(item.record_index || '').trim() || 0)).filter(n => n > 0))];
  const paintBooths = [];

  boothIndexes.forEach(index => {
    const burnerEnergy = normalizeBackendText(
      readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_burner_energy`)
    );
    const energyConsumptionUnit = normalizeFromBackendUnit(
      burnerEnergy,
      readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_energy_consumption_unit`)
    );
    const booth = {
      id: `restored-booth-${index}`,
      buildYear: readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_build_year`),
      type: normalizePaintBoothType(readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_type`)),
      separatelyHeated: normalizeBoothHeating(readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_separately_heated`)),
      burnerEnergy,
      energyConsumptionUnit,
      energyConsumption: parseNumber(readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_energy_consumption`)),
      efficiencyTech: [],
      dryingType: normalizeDryingType(readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_drying_type`)),
      dryingTemp: readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_drying_temperature`),
      dryingTime: readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_drying_time`),
      cyclesPerDay: readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_cycles_per_day`),
      idleRuns: readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_idle_runs`),
      photoFileName: readFieldValue(dataRows, 'PAINT_BOOTH', `booth_${index}_technical_plate_file_name`),
    };

    if (
      booth.buildYear || booth.type || booth.burnerEnergy || booth.energyConsumption || booth.dryingType || booth.photoFileName
    ) {
      const techFlags = [
        ['booth_1_has_wrg', 'WRG'],
        ['booth_1_has_sparschaltung', 'Sparschaltung'],
        ['booth_1_has_frequenzumformer', 'Frequenzumformer'],
      ];

      techFlags.forEach(([fieldKey, label]) => {
        const fieldName = fieldKey.replace('booth_1', `booth_${index}`);
        const value = parseBoolean(readFieldValue(dataRows, 'PAINT_BOOTH', fieldName));
        if (value) booth.efficiencyTech.push(label);
      });

      paintBooths.push(booth);
    }
  });

  parsed.paintBooths = paintBooths.length > 0 ? paintBooths : [{ id: 'restored-booth-1', ...newBooth() }];

  parsed.paintLiters = parseNumber(readFieldValue(dataRows, 'PAINT', 'paint_liters'));
  parsed.airDryingPaintShare = parseNumber(readFieldValue(dataRows, 'PAINT', 'air_drying_paint_share')) || 0;

  const materialRows = dataRows.filter(item => item.table === 'CONSUMABLE_SHARE' && item.field.endsWith('_share'));
  parsed.materials = materialRows.reduce((acc, row) => {
    const key = row.field.replace(/_share$/, '');
    if (row.value !== '') {
      acc[key] = parseNumber(row.value) || 0;
    }
    return acc;
  }, {});

  parsed.repairsPerYear = parseNumber(readFieldValue(dataRows, 'REPAIR_ACTIVITY', 'repairs_per_year'));
  parsed.repairTypes = [];
  parsed.repairTypeShares = {};
  const repairRows = dataRows.filter(item => item.table === 'REPAIR_ACTIVITY' && item.field.startsWith('repair_quota_'));
  repairRows.forEach(row => {
    const typeKey = row.field.replace('repair_quota_', '');
    const type = { spot_repair: 'Spot Repair', blech_richten: 'Blech / Richten', druecken: 'Drücken', stossstangen: 'Stossstangen', glas: 'Glas', strukturschaeden: 'Strukturschäden' }[typeKey] || typeKey.replace(/_/g, ' ');
    if (row.value !== '') {
      parsed.repairTypes.push(type);
      parsed.repairTypeShares[type] = parseNumber(row.value) || 0;
    }
  });

  const repairTypeList = ensureArray(readFieldValue(dataRows, 'REPAIR_ACTIVITY', 'repair_types'));
  if (repairTypeList.length > 0 && parsed.repairTypes.length === 0) {
    parsed.repairTypes = repairTypeList.map(normalizeDisplayText);
  }

  parsed.sparePartsTendency = normalizeDisplayText(readFieldValue(dataRows, 'REPAIR_ACTIVITY', 'spare_parts_tendency'));
  const recyclingPartsShare = parseNumber(
    readFieldValue(dataRows, 'REPAIR_ACTIVITY', 'recycling_parts_share')
  );
  parsed.recyclingPartsShare =
    recyclingPartsShare === '' ? 10 : recyclingPartsShare;
  parsed.sparePartsValueCHF = parseNumber(readFieldValue(dataRows, 'REPAIR_ACTIVITY', 'spare_parts_value'));

  parsed.fleetOwnership = normalizeBackendText(readFieldValue(dataRows, 'FLEET', 'fleet_ownership'));
  parsed.combustionCount = parseNumber(readFieldValue(dataRows, 'FLEET', 'combustion_count'));
  parsed.combustionAvgAge = parseNumber(readFieldValue(dataRows, 'FLEET', 'combustion_average_age'));
  parsed.combustionAvgKm = parseNumber(readFieldValue(dataRows, 'FLEET', 'combustion_average_km'));
  parsed.electricCount = parseNumber(readFieldValue(dataRows, 'FLEET', 'electric_count'));
  parsed.electricAvgAge = parseNumber(readFieldValue(dataRows, 'FLEET', 'electric_average_age'));
  parsed.electricAvgKm = parseNumber(readFieldValue(dataRows, 'FLEET', 'electric_average_km'));
  parsed.hybridCount = parseNumber(readFieldValue(dataRows, 'FLEET', 'hybrid_count'));
  parsed.hybridAvgAge = parseNumber(readFieldValue(dataRows, 'FLEET', 'hybrid_average_age'));
  parsed.hybridAvgKm = parseNumber(readFieldValue(dataRows, 'FLEET', 'hybrid_average_km'));

  // Legacy exports replace list separators with underscores, including within keys.
  parsed.wasteCategories = String(readFieldValue(dataRows, 'WASTE', 'waste_categories') || '')
    .match(/non_repairable_body_parts|[^_|]+/g) || [];
  parsed.bodyPartsThirdPartyShare = parseNumber(readFieldValue(dataRows, 'WASTE', 'body_parts_third_party_share')) || 0;

  parsed.hasCompressedAir = parseBoolean(readFieldValue(dataRows, 'COMPRESSED_AIR', 'has_compressed_air'));
  parsed.compressorType = readFieldValue(dataRows, 'COMPRESSED_AIR', 'compressor_type');
  parsed.compressorBuildYear = readFieldValue(dataRows, 'COMPRESSED_AIR', 'compressor_build_year');
  parsed.compressedAirPressureBar = parseNumber(readFieldValue(dataRows, 'COMPRESSED_AIR', 'compressed_air_pressure'));
  parsed.compressorHeatRecovery = readFieldValue(dataRows, 'COMPRESSED_AIR', 'compressor_heat_recovery');
  parsed.compressedAirLeaks = readFieldValue(dataRows, 'COMPRESSED_AIR', 'compressed_air_leaks');
  parsed.pneumaticGrinders = readFieldValue(dataRows, 'COMPRESSED_AIR', 'pneumatic_grinders');
  parsed.lighting = readFieldValue(dataRows, 'INFRASTRUCTURE', 'lighting');

  return parsed;
}

export function downloadCsv(csvContent, filename = 'greenone_datenerhebung.csv') {
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function saveProfileDraft(formData, options = {}) {
  const csv = buildDataCollectionCsv(formData, options);
  return {
    csv,
    status: 'local_only',
    message: 'Speichern/AWS-Upload ist in dieser Version deaktiviert.',
  };
}
